import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { formatUnit } from "@/lib/formatters";
import { CaretLeft, CaretRight, Calendar, Truck, Wrench } from "@phosphor-icons/react";

/** Weekly Schedule board — one row per truck, one column per day.
 *  Reads /api/jobs/sweeper-pins/week?start=YYYY-MM-DD and shows pinned WOs
 *  grouped by (truck, date). Cell click → jump to that day's Sweeper Radar.
 */
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (base, n) => { const d = new Date(base); d.setDate(d.getDate() + n); return d; };

// Parse an ISO date string ("YYYY-MM-DD") as a LOCAL date (not UTC). Native
// `new Date("YYYY-MM-DD")` treats the string as UTC midnight, which shifts
// the wall date back one day in any negative-offset timezone (America/*).
// This helper avoids that off-by-one so day-of-week + display date match
// the user's actual calendar.
const parseIsoLocal = (s) => {
  const [y, m, d] = String(s || "").split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

// Give us "Monday of the current week" so the grid always starts on Monday
function mondayOf(dateIso) {
  const d = parseIsoLocal(dateIso);
  const dow = d.getDay(); // 0=Sun, 1=Mon
  const diff = dow === 0 ? -6 : 1 - dow;
  return iso(addDays(d, diff));
}

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function AdminScheduleWeek() {
  const [start, setStart] = useState(() => mondayOf(iso(new Date())));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenantTz, setTenantTz] = useState("America/New_York");
  const [dragging, setDragging] = useState(null); // { jobId, fromTk, fromDate, label }
  const [hoverCell, setHoverCell] = useState(null); // "tk|date"

  useEffect(() => {
    api.get("/settings").then(r => setTenantTz(r?.data?.tenant_timezone || "America/New_York")).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get(`/jobs/sweeper-pins/week?start=${start}`)
      .then(r => setData(r.data))
      .catch(() => setData({ start, days: [], cells: {}, truck_labels: {}, totals: { by_day: {}, by_truck: {} } }))
      .finally(() => setLoading(false));
  }, [start]);

  const trucks = useMemo(() => {
    if (!data) return [];
    return Object.entries(data.truck_labels || {})
      .sort((a, b) => (a[1] || "").localeCompare(b[1] || ""));
  }, [data]);

  const days = data?.days || [];
  const cells = data?.cells || {};
  const todayIso = iso(new Date());

  const shift = (n) => setStart(iso(addDays(parseIsoLocal(start), n)));

  const fmtDayHeader = (dateIso) => {
    const d = parseIsoLocal(dateIso);
    const day = d.getDate();
    const mon = d.toLocaleString("en-US", { month: "short" });
    // Derive weekday from the actual date, NOT the column index — otherwise
    // a week starting on any day other than Monday gets mislabeled.
    return { dow: DOW[d.getDay()], date: `${mon} ${day}`, iso: dateIso };
  };

  // Drop a card onto a (truck, date) cell → re-pin the WO. Optimistic
  // update; snap back if the API call fails.
  const handleDrop = async (targetTk, targetDate) => {
    const drag = dragging;
    setDragging(null);
    setHoverCell(null);
    if (!drag) return;
    if (drag.fromTk === targetTk && drag.fromDate === targetDate) return;
    const truckLabel = data?.truck_labels?.[targetTk] || "Truck";

    // Optimistic move
    const prevData = data;
    setData(prev => {
      if (!prev) return prev;
      const nextCells = { ...prev.cells };
      const srcKey = `${drag.fromTk}|${drag.fromDate}`;
      const tgtKey = `${targetTk}|${targetDate}`;
      const src = (nextCells[srcKey] || []).filter(j => j.id !== drag.jobId);
      const card = (prev.cells[srcKey] || []).find(j => j.id === drag.jobId);
      if (!card) return prev;
      nextCells[srcKey] = src;
      nextCells[tgtKey] = [...(nextCells[tgtKey] || []), card];
      const byDay = { ...prev.totals.by_day };
      byDay[drag.fromDate] = Math.max(0, (byDay[drag.fromDate] || 0) - 1);
      byDay[targetDate] = (byDay[targetDate] || 0) + 1;
      const byTruck = { ...prev.totals.by_truck };
      if (drag.fromTk !== targetTk) {
        byTruck[drag.fromTk] = Math.max(0, (byTruck[drag.fromTk] || 0) - 1);
        byTruck[targetTk] = (byTruck[targetTk] || 0) + 1;
      }
      return { ...prev, cells: nextCells, totals: { by_day: byDay, by_truck: byTruck } };
    });

    try {
      await api.post("/jobs/sweeper-pin", {
        job_ids: [drag.jobId],
        truck_key: targetTk,
        truck_label: truckLabel,
        date: targetDate,
      });
      toast.success(`Moved to ${truckLabel} · ${targetDate}`);
    } catch (e) {
      // Roll back
      setData(prevData);
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Move failed");
    }
  };

  return (
    <AdminShell>
      <div className="p-4 md:p-6" data-testid="admin-week-schedule">
        {/* Header */}
        <div className="flex items-center gap-3 flex-wrap mb-4">
          <Calendar weight="fill" size={22} className="text-[#00C4CC]"/>
          <h1 className="font-display font-black text-2xl">Weekly Schedule</h1>
          <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
            {tenantTz.split("/").pop().replace(/_/g, " ")}
          </span>
          {/* Week / Month toggle */}
          <div className="flex rounded-lg bg-slate-100 border border-slate-200 p-0.5 gap-0.5 ml-2" data-testid="schedule-view-toggle">
            <span data-testid="schedule-view-week"
              className="h-7 px-3 rounded-md bg-white shadow-sm text-slate-900 text-[10px] font-black uppercase tracking-widest inline-flex items-center">
              Week
            </span>
            <Link to="/admin/schedule/month" data-testid="schedule-view-month"
              className="h-7 px-3 rounded-md text-slate-500 hover:text-slate-800 text-[10px] font-black uppercase tracking-widest inline-flex items-center">
              Month
            </Link>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={() => shift(-7)} data-testid="week-prev"
              className="h-9 w-9 border-2 border-slate-300 hover:border-[#0f172a] font-black flex items-center justify-center">
              <CaretLeft weight="bold" size={16}/>
            </button>
            <button onClick={() => setStart(mondayOf(iso(new Date())))} data-testid="week-today"
              className="h-9 px-3 border-2 border-slate-300 hover:border-[#0f172a] font-black uppercase text-[11px] tracking-widest">
              This Week
            </button>
            <button onClick={() => shift(7)} data-testid="week-next"
              className="h-9 w-9 border-2 border-slate-300 hover:border-[#0f172a] font-black flex items-center justify-center">
              <CaretRight weight="bold" size={16}/>
            </button>
          </div>
        </div>

        {loading && <div className="text-sm text-slate-400 p-8 text-center">Loading week…</div>}

        {!loading && trucks.length > 0 && (
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">
            💡 Drag any card to another day or truck to reschedule
          </div>
        )}

        {!loading && trucks.length === 0 && (
          <div className="rugged-card p-8 bg-white text-center" data-testid="week-empty">
            <div className="text-slate-500 font-bold">No WOs pinned for this week.</div>
            <Link to="/admin/lineup" className="mt-3 inline-block h-10 px-4 bg-[#00C4CC] text-white font-black uppercase text-xs tracking-widest rounded">
              Open Sweeper Radar →
            </Link>
          </div>
        )}

        {!loading && trucks.length > 0 && (
          <div className="rugged-card bg-white overflow-x-auto" data-testid="week-grid">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left p-3 font-black uppercase text-[11px] tracking-widest text-slate-700 w-48">Truck</th>
                  {days.map((d) => {
                    const h = fmtDayHeader(d);
                    const isToday = h.iso === todayIso;
                    return (
                      <th key={d} className={`p-2 min-w-[110px] font-black uppercase text-[11px] tracking-widest ${isToday ? "text-[#00C4CC]" : "text-slate-600"}`}>
                        <Link to={`/admin/lineup?date=${d}`} className="hover:underline block">
                          <div>{h.dow}</div>
                          <div className="text-[10px] opacity-75">{h.date}</div>
                          <div className="text-[9px] mt-0.5 opacity-60">{data.totals.by_day[d] || 0} WOs</div>
                        </Link>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {trucks.map(([tk, name]) => (
                  <tr key={tk} className="border-t border-slate-200 hover:bg-slate-50">
                    <td className="p-3 align-top">
                      <div className="flex items-center gap-2">
                        {tk.startsWith("sub:") ? <Wrench weight="fill" size={14} className="text-fuchsia-600"/> : <Truck weight="fill" size={14} className="text-slate-500"/>}
                        <span className="font-bold text-[13px]">{name}</span>
                      </div>
                      <div className="mt-0.5 text-[10px] text-slate-500 uppercase tracking-widest">{data.totals.by_truck[tk] || 0} WOs</div>
                    </td>
                    {days.map(d => {
                      const list = cells[`${tk}|${d}`] || [];
                      const isToday = d === todayIso;
                      const cellKey = `${tk}|${d}`;
                      const isHover = hoverCell === cellKey && dragging;
                      return (
                        <td key={d}
                          onDragOver={(e) => { if (dragging) { e.preventDefault(); setHoverCell(cellKey); } }}
                          onDragLeave={() => { if (hoverCell === cellKey) setHoverCell(null); }}
                          onDrop={(e) => { e.preventDefault(); handleDrop(tk, d); }}
                          data-testid={`week-drop-${tk}-${d}`}
                          className={`p-1.5 align-top border-l border-slate-100 ${isToday ? "bg-cyan-50/40" : ""} ${isHover ? "ring-2 ring-[#00C4CC] bg-cyan-100/60 rounded" : ""}`}>
                          {list.length === 0 ? (
                            <Link to={`/admin/lineup?date=${d}`}
                              className="block h-full min-h-[52px] text-[10px] text-slate-300 hover:text-slate-500 text-center italic pt-2">
                              {isHover ? "Drop here" : "—"}
                            </Link>
                          ) : (
                            <div className="space-y-1" data-testid={`week-cell-${tk}-${d}`}>
                              {list.slice(0, 4).map(j => (
                                <div key={j.id}
                                  draggable
                                  onDragStart={(e) => {
                                    e.dataTransfer.effectAllowed = "move";
                                    setDragging({ jobId: j.id, fromTk: tk, fromDate: d, label: j.wo || j.title });
                                  }}
                                  onDragEnd={() => { setDragging(null); setHoverCell(null); }}
                                  className={`${dragging?.jobId === j.id ? "opacity-40" : ""} cursor-grab active:cursor-grabbing`}>
                                  <Link to={`/admin/jobs/${j.id}`}
                                    className={`block p-1.5 rounded text-[10px] leading-tight ${j.is_turn ? "bg-fuchsia-100 hover:bg-fuchsia-200 border-l-2 border-fuchsia-600" :
                                      j.priority === "urgent" || j.priority === "emergency" ? "bg-red-100 hover:bg-red-200 border-l-2 border-red-600" :
                                      "bg-slate-100 hover:bg-slate-200 border-l-2 border-slate-400"}`}
                                    data-testid={`week-job-${j.id}`}
                                    onDragStart={(e) => e.preventDefault()}
                                    title={`Drag to move · ${j.wo ? "WO " + j.wo + " · " : ""}${j.title || j.addr}`}>
                                    <div className="font-black truncate">{j.wo || "—"}</div>
                                    <div className="text-slate-700 truncate">{j.addr || j.title}{j.unit ? ` · ${formatUnit(j.unit)}` : ""}</div>
                                  </Link>
                                </div>
                              ))}
                              {list.length > 4 && (
                                <Link to={`/admin/lineup?date=${d}`}
                                  className="block text-center text-[10px] font-bold text-[#00C4CC] hover:underline pt-0.5">
                                  +{list.length - 4} more →
                                </Link>
                              )}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminShell>
  );
}
